import { useEffect, useState } from "react";
import { useLocation, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useLang } from "../contexts/LangContext";
import { useAuth } from "../contexts/AuthContext";
import { Check, X, Loader } from "lucide-react";

const MAX_POLLS = 10;
const POLL_INTERVAL = 2000;

export default function BookingSuccess() {
  const { t } = useLang();
  const { user, loading } = useAuth();
  const location = useLocation();
  const [status, setStatus] = useState({ phase: "polling", payment_status: null, amount: null });

  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const sessionId = params.get("session_id");
    if (!sessionId) { setStatus({ phase: "missing" }); return; }
    if (loading) return;

    let attempts = 0;
    let timer = null;
    const poll = async () => {
      try {
        const r = await api.get(`/checkout/status/${sessionId}`);
        if (r.data.payment_status === "paid") {
          setStatus({ phase: "done", payment_status: "paid", amount: r.data.amount });
          return;
        }
        if (r.data.status === "expired" || r.data.payment_status === "failed") {
          setStatus({ phase: "done", payment_status: "failed" });
          return;
        }
        if (++attempts >= MAX_POLLS) {
          setStatus({ phase: "timeout", payment_status: r.data.payment_status });
          return;
        }
        timer = setTimeout(poll, POLL_INTERVAL);
      } catch (e) {
        setStatus({ phase: "error" });
      }
    };
    poll();
    return () => { if (timer) clearTimeout(timer); };
  }, [location.search, loading]);

  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      {status.phase === "polling" && (
        <>
          <Loader className="w-12 h-12 mx-auto text-[#FF5A00] animate-spin mb-6" />
          <h1 className="text-3xl font-black tracking-tighter mb-3">Zahlung wird geprüft...</h1>
          <p className="text-neutral-600">Einen Moment bitte, wir bestätigen Ihre Zahlung bei Stripe.</p>
        </>
      )}
      {status.phase === "done" && status.payment_status === "paid" && (
        <>
          <div className="w-16 h-16 bg-emerald-500 text-white rounded-full flex items-center justify-center mx-auto mb-6">
            <Check size={32} />
          </div>
          <h1 className="text-4xl font-black tracking-tighter mb-3" data-testid="payment-success-title">Zahlung erfolgreich!</h1>
          <p className="text-neutral-600">Ihre Anzahlung von <strong>{status.amount?.toFixed(2)} €</strong> wurde erfolgreich gebucht. Ihre Buchung ist jetzt bestätigt.</p>
          <Link to="/dashboard" className="btn-primary inline-flex mt-8">Meine Buchungen ansehen</Link>
        </>
      )}
      {(status.phase === "done" && status.payment_status !== "paid") || status.phase === "error" || status.phase === "missing" || status.phase === "timeout" ? (
        <>
          <div className="w-16 h-16 bg-neutral-300 text-white rounded-full flex items-center justify-center mx-auto mb-6">
            <X size={32} />
          </div>
          <h1 className="text-3xl font-black tracking-tighter mb-3">Zahlung nicht abgeschlossen</h1>
          <p className="text-neutral-600">Falls Sie versehentlich abgebrochen haben, können Sie die Anzahlung später aus dem Dashboard nachholen.</p>
          <Link to="/dashboard" className="btn-primary inline-flex mt-8">Zum Dashboard</Link>
        </>
      ) : null}
    </div>
  );
}
